<template>
  <div class="checklist-app">
    <!-- Toast -->
    <div class="toast" :class="{ show: toastMsg }">{{ toastMsg }}</div>

    <!-- Header -->
    <header class="page-header">
      <span class="brand">续费社群 · 清单</span>
      <span class="username">{{ userName }}</span>
    </header>

    <!-- Loading -->
    <div v-if="loading" class="content">
      <div class="empty-state">
        <span class="empty-icon">⏳</span>
        <span>加载中...</span>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="errorMsg" class="content">
      <div class="error-card">
        <span class="empty-icon">⚠️</span>
        <h3>{{ errorMsg }}</h3>
        <p v-if="errorDetail" style="font-size:12px;color:#b8a99a;margin-top:6px;">{{ errorDetail }}</p>
      </div>
    </div>

    <!-- Form -->
    <div v-else-if="sessionData" class="content">
      <div class="form-card">
        <!-- Group name -->
        <div class="group-label">{{ sessionData.groupName }}</div>

        <!-- Session type -->
        <div class="session-type" :class="sessionData.session.type === 'survey' ? 'type-survey' : 'type-confirm'">
          {{ sessionData.session.type === 'survey' ? '📋 D1 调研' : '📋 D28 确认' }}
        </div>

        <!-- Instruction -->
        <div class="instruction">
          <template v-if="sessionData.session.type === 'survey'">
            请勾选你目前还<strong>没有完成</strong>的项目
          </template>
          <template v-else>
            <template v-if="sessionData.d1GapCount != null">
              D1 你报了 <strong>{{ sessionData.d1GapCount }} 个缺口</strong>，请勾选其中已补上的项目
            </template>
            <template v-else>
              请勾选你<strong>已完成</strong>的项目
            </template>
          </template>
        </div>
        <div class="hint-text" v-if="sessionData.session.type === 'survey'">
          勾选 = 你还没做，需要提醒
        </div>
        <div class="hint-text" v-else>
          <template v-if="sessionData.d1GapCount != null">
            勾选 = 这项你已经补上了
          </template>
          <template v-else>
            勾选 = 你已经完成了
          </template>
        </div>

        <!-- Already submitted hint -->
        <div v-if="sessionData.myResponse" class="submitted-hint">
          📝 你已提交过，可以修改后重新提交
        </div>

        <!-- Checklist items -->
        <div class="checklist-items">
          <!-- Core items (case records + defense) -->
          <label
            v-for="item in coreItems"
            :key="item.key"
            class="checklist-item"
            :class="{ checked: checkedItems[item.key] }"
          >
            <input
              type="checkbox"
              :checked="checkedItems[item.key]"
              @change="toggleItem(item.key)"
              class="check-box"
            />
            <span class="check-label">{{ item.label }}</span>
          </label>

          <!-- Notes section (6期), collapsible -->
          <div v-if="noteItems.length > 0" class="notes-section">
            <div class="notes-toggle" @click="notesExpanded = !notesExpanded">
              <span class="toggle-icon">{{ notesExpanded ? '▼' : '▶' }}</span>
              <span>心得部分（6期学员填写）</span>
            </div>
            <div v-show="notesExpanded" class="notes-items">
              <label
                v-for="item in noteItems"
                :key="item.key"
                class="checklist-item"
                :class="{ checked: checkedItems[item.key] }"
              >
                <input
                  type="checkbox"
                  :checked="checkedItems[item.key]"
                  @change="toggleItem(item.key)"
                  class="check-box"
                />
                <span class="check-label">{{ item.label }}</span>
              </label>
            </div>
          </div>
        </div>

        <!-- Submit button -->
        <button
          class="btn btn-submit"
          :disabled="submitting"
          @click="doSubmit"
        >
          {{ submitting ? '提交中...' : submitted ? '🔄 重新提交' : '📩 提交' }}
        </button>

        <!-- Success message -->
        <div v-if="submitted" class="success-msg">✅ 已提交</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();

// ═══════════════ User ═══════════════
const rawUser = localStorage.getItem("rc_user");
const user = rawUser ? JSON.parse(rawUser) : { name: "教练" };
const userName = user.name;

// ═══════════════ State ═══════════════
const tokenParam = computed(() => route.params.token as string);
const sessionData = ref<any>(null);
const loading = ref(true);
const errorMsg = ref("");
const errorDetail = ref("");
const checkedItems = reactive<Record<string, boolean>>({});
const submitting = ref(false);
const submitted = ref(false);
const toastMsg = ref("");
const notesExpanded = ref(false);

// Split items: first 4 are core (case records + defense), rest are notes (6期)
const coreItems = computed(() => (sessionData.value?.session?.items || []).slice(0, 4));
const noteItems = computed(() => (sessionData.value?.session?.items || []).slice(4));

// ═══════════════ Helpers ═══════════════
function toast(msg: string) {
  toastMsg.value = msg;
  setTimeout(() => { toastMsg.value = ""; }, 2200);
}

function getHeaders(): Record<string, string> {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("rc_token") ?? ""}`
  };
}

// ═══════════════ Load Session ═══════════════
async function loadSession() {
  loading.value = true;
  errorMsg.value = "";
  errorDetail.value = "";
  try {
    const res = await fetch(`/api/checklist/session/${tokenParam.value}`, {
      headers: getHeaders()
    });
    const data = await res.json();

    if (!res.ok || data.error) {
      const msg = data.error || "加载失败";
      if (msg.includes("不是该小组成员") || msg.includes("member")) {
        errorMsg.value = "你没有权限访问此清单";
        errorDetail.value = "只有该小组成员才能填写清单";
      } else if (msg.includes("window") || msg.includes("窗口") || msg.includes("closed") || msg.includes("已关闭")) {
        errorMsg.value = "填写窗口已关闭";
        errorDetail.value = "清单填写时间已过，无法再提交";
      } else if (msg.includes("not found") || msg.includes("不存在") || msg.includes("无效")) {
        errorMsg.value = "链接无效或已过期";
        errorDetail.value = "请确认链接是否正确";
      } else {
        errorMsg.value = msg;
      }
      return;
    }

    sessionData.value = data;

    // Init checked items from existing response or default to all false
    if (data.myResponse && data.myResponse.items) {
      for (const item of data.myResponse.items) {
        checkedItems[item.key] = item.completed ?? false;
      }
      submitted.value = true;
    } else {
      for (const item of data.session.items) {
        checkedItems[item.key] = false;
      }
    }
  } catch (e: any) {
    errorMsg.value = "网络错误，请稍后重试";
    errorDetail.value = e.message || "";
  } finally {
    loading.value = false;
  }
}

// ═══════════════ Toggle Item ═══════════════
function toggleItem(key: string) {
  checkedItems[key] = !checkedItems[key];
}

// ═══════════════ Submit ═══════════════
async function doSubmit() {
  if (submitting.value) return;
  submitting.value = true;
  try {
    const items = Object.entries(checkedItems).map(([key, completed]) => ({
      key,
      completed
    }));
    const res = await fetch(`/api/checklist/session/${tokenParam.value}/submit`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({ items })
    });
    const data = await res.json();

    if (!res.ok || data.error) {
      toast(data.error || "提交失败");
      return;
    }

    submitted.value = true;
    toast("✅ 已提交");
  } catch {
    toast("提交失败，请稍后重试");
  } finally {
    submitting.value = false;
  }
}

// ═══════════════ Init ═══════════════
onMounted(() => {
  loadSession();
});
</script>

<style scoped>
/* ===== Design tokens ===== */
:root {
  --primary: #c0835a; --primary-light: #fdf6f0; --primary-dark: #9e6a42;
}
* { margin: 0; padding: 0; box-sizing: border-box; }

.checklist-app {
  width: 100%; max-width: 430px; height: 100%;
  background: #fef9f2; border-radius: 28px; overflow: hidden;
  box-shadow: 0 12px 40px rgba(61,53,41,0.14);
  margin: 0 auto; position: relative; display: flex; flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif;
  color: #3d3529;
  min-height: 100vh;
}

/* Header */
.page-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; background: #fff;
  border-bottom: 1px solid #f0e8dd;
  flex-shrink: 0;
}
.brand { font-weight: 700; font-size: 16px; color: #e8894f; }
.username { font-size: 13px; color: #8b7e6a; }

/* Content */
.content {
  flex: 1; overflow-y: auto; padding: 20px;
  display: flex; flex-direction: column;
}

/* Empty / Error */
.empty-state {
  text-align: center; padding: 40px 16px;
  border: 1.5px dashed #f0e8dd; border-radius: 14px;
  color: #b8a99a; font-size: 14px; line-height: 1.6;
}
.empty-icon { font-size: 36px; display: block; margin-bottom: 8px; }

.error-card {
  text-align: center; padding: 32px 20px;
  background: #fff; border-radius: 14px;
  border: 1px solid #f0e8dd;
}
.error-card h3 {
  font-size: 16px; font-weight: 700; color: #d4786e; margin-top: 8px;
}
.error-card p {
  line-height: 1.6;
}

/* Form card */
.form-card {
  background: #fff; border-radius: 14px; padding: 24px 20px;
  border: 1px solid #f0e8dd;
}

/* Group label */
.group-label {
  font-size: 15px; font-weight: 700; color: #3d3529; margin-bottom: 8px;
  text-align: center;
}

/* Session type badge */
.session-type {
  text-align: center; font-size: 14px; font-weight: 700;
  padding: 8px 16px; border-radius: 20px; margin-bottom: 16px;
}
.type-survey { background: #fef5ee; color: #e8894f; }
.type-confirm { background: #f0f4ff; color: #6b5ea4; }

/* Instruction */
.instruction {
  font-size: 15px; color: #5c5040; text-align: center; margin-bottom: 4px;
  line-height: 1.6;
}
.instruction strong { color: #3d3529; }

.hint-text {
  font-size: 12px; color: #b8a99a; text-align: center; margin-bottom: 16px;
}

/* Already submitted hint */
.submitted-hint {
  font-size: 13px; color: #8b7e6a; text-align: center; margin-bottom: 14px;
  background: #fef9f2; padding: 8px 12px; border-radius: 8px;
}

/* Notes section */
.notes-section {
  margin-top: 6px;
}
.notes-toggle {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; border-radius: 10px;
  background: #f4f2fa; color: #8b7ec4; font-size: 13px; font-weight: 600;
  cursor: pointer; user-select: none; transition: background 0.2s;
}
.notes-toggle:hover { background: #ece8f5; }
.toggle-icon { font-size: 10px; width: 12px; }
.notes-items {
  margin-top: 6px; display: flex; flex-direction: column; gap: 6px;
}

/* Checklist items */
.checklist-items {
  display: flex; flex-direction: column; gap: 6px; margin-bottom: 20px;
}

.checklist-item {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 16px; border-radius: 10px;
  border: 1.5px solid #f0e8dd; cursor: pointer;
  transition: all 0.2s; user-select: none;
  background: #fefefe;
}
.checklist-item:hover { border-color: #d4c8b8; background: #fefcf9; }
.checklist-item.checked {
  border-color: #c0835a; background: #fdf6f0;
}

.check-box {
  width: 20px; height: 20px; flex-shrink: 0;
  accent-color: #c0835a; cursor: pointer;
}

.check-label {
  font-size: 14px; color: #3d3529; line-height: 1.5; flex: 1;
}

/* Submit button */
.btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 14px 24px; border-radius: 26px; font-size: 15px; font-weight: 600;
  cursor: pointer; border: none; transition: all 0.2s; font-family: inherit; width: 100%;
}
.btn-submit {
  background: linear-gradient(135deg, #c0835a, #d4a843); color: #fff;
}
.btn-submit:active { opacity: 0.85; transform: scale(0.98); }
.btn-submit:disabled { opacity: 0.5; cursor: not-allowed; }

/* Success message */
.success-msg {
  text-align: center; margin-top: 12px;
  font-size: 15px; font-weight: 700; color: #3d7a5a;
}

/* Toast */
.toast {
  position: fixed; bottom: 60px; left: 50%; transform: translateX(-50%);
  background: #3d3529; color: #fff; padding: 12px 24px; border-radius: 24px;
  font-size: 14px; font-weight: 600; z-index: 100; opacity: 0;
  transition: opacity 0.3s; pointer-events: none;
}
.toast.show { opacity: 1; }
</style>
